import { useQueryClient } from '@tanstack/react-query';
import { Copy, Expand, Eye, EyeOff } from 'lucide-react';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useInRouterContext, useLocation } from 'react-router';
import { useApi } from '../api/context';
import type { RevealedSecret } from '../api/types';
import { useToast } from '../components/toast';
import { Dialog } from '../components/ui';
import { formatDateTime, formatRelative } from '../lib/format';
import { strings } from '../strings';
import type { ValueCell } from './model';
import { locationEntry } from './registry';
import { RevealConfirm, type RevealChoice } from './RevealConfirm';
import {
  entryReference,
  INLINE_CHARACTERS,
  isReasonRequired,
  isReasonRequiredError,
  markReasonRequired,
  needsConfirmation,
  onRevealSignOut,
  reasonProblems,
  reasonToSend,
  REVEAL_SECONDS,
  revealFailure,
  skipConfirmation,
  useRevealEnvironment,
} from './reveal';

const r = strings.reveal;

type RevealableCell = Extract<ValueCell, { kind: 'secret-revealable' }>;

type Phase =
  | { phase: 'masked' }
  | { phase: 'confirm'; problems: string[]; reason: string; anchor: HTMLElement | null }
  | { phase: 'loading' }
  // The value lives here only, while it is shown (spec 6.10.18).
  | { phase: 'revealed'; secret: RevealedSecret; until: number }
  | { phase: 'failed'; short: string; detail: string };

/** Calls `onChange` when the route changes (outside a router it does nothing). */
function RouteWatcher({ onChange }: { onChange: () => void }) {
  const location = useLocation();
  const key = `${location.pathname}?${location.search}`;
  const first = useRef(key);
  useEffect(() => {
    if (key !== first.current) {
      first.current = key;
      onChange();
    }
  }, [key, onChange]);
  return null;
}

/** The countdown ring and the seconds left. */
function Countdown({ left }: { left: number }) {
  const radius = 6;
  const circumference = 2 * Math.PI * radius;
  return (
    <span className="var-countdown" role="timer" aria-label={r.secondsLabel(left)}>
      <svg viewBox="0 0 16 16" className="size-4" aria-hidden>
        <circle cx="8" cy="8" r={radius} className="var-countdown-track" />
        <circle
          cx="8"
          cy="8"
          r={radius}
          className="var-countdown-ring"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - left / REVEAL_SECONDS)}
          transform="rotate(-90 8 8)"
        />
      </svg>
      <span aria-hidden>{r.seconds(left)}</span>
    </span>
  );
}

/**
 * A secret value that can be revealed (spec 6.10.18): masked with Reveal; the confirmation; the value for 30
 * seconds with the countdown, Copy, Hide and Expand; failures with Retry. The value exists only in this
 * component's state while it is shown. Keyed by deployment and secret, so the row of another deployment (the same
 * row key on the same page) never inherits its state or a request in flight.
 */
export function RevealCell(props: { cell: RevealableCell; tabbable?: boolean; now?: number }) {
  const env = useRevealEnvironment();
  return <RevealCellState key={`${env?.deploymentId ?? ''}|${props.cell.secretName}`} {...props} />;
}

function RevealCellState({
  cell,
  tabbable = false,
  now,
}: {
  cell: RevealableCell;
  /** Buttons are Tab stops (details pane); in grids they are reached through the cell's keys. */
  tabbable?: boolean;
  now?: number;
}) {
  const env = useRevealEnvironment();
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const inRouter = useInRouterContext();
  const ids = useId();
  const [state, setState] = useState<Phase>({ phase: 'masked' });
  const [left, setLeft] = useState(REVEAL_SECONDS);
  const [viewer, setViewer] = useState(false);
  const container = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const request = useRef<AbortController | null>(null);
  const viewerOpen = useRef(false);
  // The hide triggers apply while the value is shown, while its request runs (the answer is dropped) and while
  // a failure shows (the badge is cleared).
  const watching = state.phase === 'revealed' || state.phase === 'loading' || state.phase === 'failed';
  const phase = useRef(state.phase);
  const display = cell.display;
  const canReveal = !!env?.canReveal;
  const tabIndex = tabbable ? 0 : -1;
  const scopeKey = env?.scopeKey ?? '';
  const currentScope = useRef(scopeKey);
  useLayoutEffect(() => {
    phase.current = state.phase;
    currentScope.current = scopeKey;
  });

  useEffect(() => () => request.current?.abort(), []);
  useEffect(() => {
    viewerOpen.current = viewer;
  }, [viewer]);

  /**
   * A hide trigger: drops the value from state and the DOM, aborts a request in flight and clears a failure;
   * `sync` applies it before the browser can keep the page. Only a value that was shown is announced.
   */
  const hide = useCallback(
    (options: { sync?: boolean; announce?: boolean } = {}) => {
      const shown = phase.current === 'revealed';
      request.current?.abort();
      request.current = null;
      const apply = () => {
        setState((current) =>
          current.phase === 'revealed' || current.phase === 'loading' || current.phase === 'failed'
            ? { phase: 'masked' }
            : current,
        );
        setViewer(false);
      };
      if (options.sync) flushSync(apply);
      else apply();
      if (shown && options.announce !== false) toast.show({ tone: 'info', title: r.hiddenAgain(display) });
    },
    [display, toast],
  );

  const run = (reason: string) => {
    if (!env) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const askedIn = currentScope.current;
    setState({ phase: 'loading' });
    api
      .revealSecret(
        env.deploymentId,
        cell.secretName,
        { purpose: 'view', reason: reasonToSend(reason) },
        controller.signal,
      )
      .then(
        (secret) => {
          if (controller.signal.aborted) return;
          request.current = null;
          void queryClient.invalidateQueries({ queryKey: ['deployment', env.deploymentId, 'activity'] });
          // Shown only in the scope it was asked for and on a visible page (the triggers normally abort first).
          if (currentScope.current !== askedIn || document.visibilityState === 'hidden') {
            setState({ phase: 'masked' });
            return;
          }
          setLeft(REVEAL_SECONDS);
          setState({ phase: 'revealed', secret, until: Date.now() + REVEAL_SECONDS * 1000 });
        },
        (error: unknown) => {
          if (controller.signal.aborted) return;
          request.current = null;
          if (isReasonRequiredError(error)) markReasonRequired(env.user);
          const problems = reasonProblems(error);
          if (problems.length) setState({ phase: 'confirm', problems, reason, anchor: button.current });
          else setState({ phase: 'failed', ...revealFailure(error) });
        },
      );
  };

  const start = () => {
    if (!env || !canReveal || state.phase === 'loading') return;
    if (needsConfirmation(env.user))
      setState({ phase: 'confirm', problems: [], reason: '', anchor: button.current });
    else run('');
  };

  const confirmed = (choice: RevealChoice) => {
    if (env && choice.dontAsk) skipConfirmation(env.user);
    run(choice.reason);
  };

  // Hide triggers (spec 6.10.18): the timer, Esc and leaving the visible area only while the value is shown;
  // the hidden tab, pagehide and sign-out also while a request runs or a failure shows.
  useEffect(() => {
    if (!watching) return;
    const element = container.current;
    const until = state.phase === 'revealed' ? state.until : null;
    // One hide per reveal, whichever trigger comes first.
    let done = false;
    const once = (options?: { sync?: boolean; announce?: boolean }) => {
      if (done) return;
      done = true;
      hide(options);
    };
    let interval: number | undefined;
    if (until !== null) {
      const tick = () => {
        const seconds = Math.ceil((until - Date.now()) / 1000);
        if (seconds <= 0 || !element?.isConnected) once();
        else setLeft(seconds);
      };
      interval = window.setInterval(tick, 250);
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') once({ sync: true });
    };
    const onPageHide = () => once({ sync: true, announce: false });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && until !== null && !viewerOpen.current) once();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('keydown', onKey, true);
    const stopSignOut = onRevealSignOut(() => once({ sync: true, announce: false }));
    // Scrolled out of its container, or its window hidden (no longer laid out).
    let observer: IntersectionObserver | null = null;
    if (until !== null && element && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => !entry.isIntersecting)) once();
      });
      observer.observe(element);
    }
    return () => {
      if (interval !== undefined) window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('keydown', onKey, true);
      stopSignOut();
      observer?.disconnect();
    };
  }, [state, watching, hide]);

  // A change of the window's deployment, scope or view (a request in flight is dropped too).
  const scopeSeen = useRef(scopeKey);
  useEffect(() => {
    if (scopeSeen.current === scopeKey) return;
    scopeSeen.current = scopeKey;
    if (watching) hide();
  }, [scopeKey, watching, hide]);
  const onRoute = useCallback(() => {
    if (watching) hide();
  }, [watching, hide]);

  const copyShown = () => {
    if (state.phase !== 'revealed') return;
    void navigator.clipboard
      ?.writeText(state.secret.value)
      .then(() => toast.show({ tone: 'success', title: r.copiedShown }))
      .catch(() => toast.show({ tone: 'error', title: r.copyFailed }));
  };

  const meta = (
    <>
      {cell.version && <span className="font-mono text-[12px]">{cell.version}</span>}
      <span className="truncate text-[12px] text-fg-muted" title={formatDateTime(cell.at)}>
        · {formatRelative(cell.at, now)}{' '}
        {cell.by ? strings.common.by(cell.by) : strings.variables.value.outsideNebisys}
      </span>
    </>
  );

  let content: ReactNode;
  if (state.phase === 'revealed') {
    const { secret } = state;
    const long = secret.value.length > INLINE_CHARACTERS;
    const inline = long ? `${secret.value.slice(0, INLINE_CHARACTERS)}…` : secret.value;
    const version = locationEntry(cell.location).formatVersion(secret.version);
    content = (
      <span className="var-reveal" data-testid="revealed-value">
        <span className="var-value">
          <span className="sr-only">{r.revealedLabel(display)}</span>
          <code className="var-revealed">{inline}</code>
        </span>
        {/* The controls and the reference on a second line, so narrow columns keep the value readable. */}
        <span className="var-reveal-controls">
          {long && (
            <button
              type="button"
              tabIndex={tabIndex}
              className="btn btn-ghost btn-icon"
              aria-label={r.expandLabel(display)}
              aria-keyshortcuts="Control+Enter"
              title={`${r.expand} (Ctrl+Enter)`}
              data-reveal-expand
              onClick={() => setViewer(true)}
            >
              <Expand aria-hidden className="size-3.5" />
            </button>
          )}
          <Countdown left={left} />
          <button
            type="button"
            tabIndex={tabIndex}
            className="btn btn-ghost btn-icon"
            aria-label={r.copy(display)}
            title={r.copy(display)}
            data-reveal-copy
            onClick={copyShown}
          >
            <Copy aria-hidden className="size-3.5" />
          </button>
          <button
            type="button"
            tabIndex={tabIndex}
            className="btn btn-ghost btn-icon"
            aria-label={r.hide(display)}
            title={r.hide(display)}
            data-reveal-hide
            onClick={() => hide()}
          >
            <EyeOff aria-hidden className="size-3.5" />
          </button>
          <span
            className="var-reveal-meta truncate text-[11.5px] text-fg-muted"
            title={r.entryTitle(secret.version, secret.auditEventId)}
          >
            {r.version(version)} · {r.entry(entryReference(secret.auditEventId))}
            {!secret.recorded && ` · ${r.outside}`}
          </span>
        </span>
      </span>
    );
  } else {
    const loading = state.phase === 'loading';
    const reasonId = `${ids}-denied`;
    content = (
      <span className="var-value">
        <span className="var-mask" aria-hidden>
          {r.mask}
        </span>
        <span className="sr-only">{cell.label}</span>
        <button
          ref={button}
          type="button"
          tabIndex={tabIndex}
          className="btn btn-sm var-reveal-button"
          data-reveal-button
          aria-label={r.buttonLabel(display)}
          aria-disabled={!canReveal || loading ? true : undefined}
          aria-describedby={!canReveal ? reasonId : undefined}
          title={canReveal ? r.buttonHint : env?.deniedReason}
          onClick={(event) => {
            event.stopPropagation();
            start();
          }}
        >
          <Eye aria-hidden className="size-3.5" />
          {loading ? r.revealing : r.button}
        </button>
        {!canReveal && env && (
          <span id={reasonId} className="sr-only">
            {env.deniedReason}
          </span>
        )}
        {state.phase === 'failed' ? (
          <>
            <span className="badge tone-danger truncate" title={state.detail} data-testid="reveal-failure">
              {state.short}
            </span>
            <button
              type="button"
              tabIndex={tabIndex}
              className="btn btn-sm"
              aria-label={`${r.retry}: ${display}`}
              onClick={start}
            >
              {r.retry}
            </button>
          </>
        ) : (
          meta
        )}
      </span>
    );
  }

  return (
    <span ref={container} className="var-reveal-host" data-reveal-state={state.phase}>
      {content}
      {inRouter && watching && <RouteWatcher onChange={onRoute} />}
      {state.phase === 'confirm' && env && (
        <RevealConfirm
          name={display}
          location={cell.location}
          account={env.account}
          purpose="view"
          anchor={state.anchor}
          required={isReasonRequired()}
          problems={state.problems}
          busy={false}
          initialReason={state.reason}
          onConfirm={confirmed}
          onCancel={() => setState({ phase: 'masked' })}
        />
      )}
      {viewer &&
        state.phase === 'revealed' &&
        createPortal(
          <Dialog
            title={r.viewerTitle(display)}
            onClose={() => setViewer(false)}
            footer={
              <>
                <span className="mr-auto self-center">
                  <Countdown left={left} />
                </span>
                <button type="button" className="btn" aria-label={r.copy(display)} onClick={copyShown}>
                  <Copy aria-hidden className="size-4" />
                  {r.copyShort}
                </button>
                <button type="button" className="btn" aria-label={r.hide(display)} onClick={() => hide()}>
                  <EyeOff aria-hidden className="size-4" />
                  {r.hideShort}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-autofocus
                  onClick={() => setViewer(false)}
                >
                  {r.close}
                </button>
              </>
            }
          >
            <pre className="var-revealed var-revealed-full">{state.secret.value}</pre>
          </Dialog>,
          document.body,
        )}
    </span>
  );
}
